// Handlers can return false to indicate "not handled" so the dispatcher
// skips preventDefault — useful for Escape, where the binding may decide
// at runtime not to consume the key (e.g. there is no fullscreen to exit).
// Any non-`false` return (including void/undefined) is treated as
// "consumed". The unknown return type lets handlers be expression-bodied
// arrows that pass through whatever the inner method returned without
// requiring a wrapper block.
export type ShortcutHandler = () => unknown;

export interface ShortcutMap {
  readonly [key: string]: ShortcutHandler;
}

// Binds keydown shortcuts on the target. Each handler is invoked when the
// corresponding key is pressed, and only if no Ctrl/Meta/Alt modifier is
// active (we do not want to intercept browser shortcuts like Cmd+P/Ctrl+R)
// and the focus is not in an input/textarea/contenteditable element.
//
// Map keys can be either lower- or upper-case single chars (compared
// case-insensitively against e.key) or the literal 'Escape'/'Enter'/'Space'.
export function bindShortcuts(target: Window | HTMLElement, map: ShortcutMap): () => void {
  const lookup = new Map<string, ShortcutHandler>();
  for (const [key, handler] of Object.entries(map)) {
    lookup.set(key.toLowerCase(), handler);
  }

  const handler = (event: Event): void => {
    const e = event as KeyboardEvent;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const targetEl = e.target as HTMLElement | null;
    if (targetEl !== null) {
      const tag = targetEl.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || targetEl.isContentEditable) return;
    }
    const action = lookup.get(e.key.toLowerCase());
    if (action !== undefined) {
      const handled = action();
      // preventDefault only if handler returns void/undefined/true (i.e. it
      // consumed the key). Returning `false` means "not handled" — let the
      // browser/AT default fire (e.g. Escape that didn't exit fullscreen).
      if (handled !== false) {
        e.preventDefault();
      }
    }
  };

  target.addEventListener('keydown', handler);
  return () => target.removeEventListener('keydown', handler);
}
